import React from 'react';

import {
  Box,
  FormControlLabel,
  Checkbox,
  IconButton,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

import TableCheckbox from 'components/shared/CustomTable/TableCheckbox';
import CurrentTableContext from 'components/shared/CustomTable/context';

import type { Column } from 'api/shared/models';
import { palette } from 'themeV5/palette';
import MultipleSort from './MultipleSort';

const stylesIcon: React.CSSProperties = {
  cursor: 'pointer',
};

interface Props {
  column: Column;
  createSortHandler: (property: string) => (event: any) => void;
}

const CustomTableCellBody = ({ column, createSortHandler }: Props) => {
  const {
    order,
    orderBy,
    variant,
    disabled,
    checkboxColor = 'primary',
    isCheckedAll,
    indeterminateCheck,
    handleCheckedAll,
    handleColumnCollapsed,
  } = React.useContext(CurrentTableContext);

  if (column.sort) {
    return (
      <Box
        display={'flex'}
        style={{ flexBasis: column.flexBasis }}
        justifyContent={'center'}
        alignItems="center"
      >
        {column.label}
        {column.sort &&
          (order === 'desc' && orderBy === column.apiField ? (
            <IconButton
              size="small"
              onClick={createSortHandler(column.apiField)}
            >
              <ArrowUpwardIcon
                style={{
                  ...stylesIcon,
                  color:
                    variant === 'primary'
                      ? palette.gray[200]
                      : palette.dark.main,
                  opacity: orderBy === column.apiField ? 1 : 0.7,
                }}
              />
            </IconButton>
          ) : (
            <IconButton
              size="small"
              onClick={createSortHandler(column.apiField)}
            >
              <ArrowDownwardIcon
                style={{
                  ...stylesIcon,
                  color:
                    variant === 'primary'
                      ? palette.gray[200]
                      : palette.dark.main,
                  opacity: orderBy === column.apiField ? 1 : 0.6,
                }}
              />
            </IconButton>
          ))}
      </Box>
    );
  }
  if (column.checkbox) {
    return (
      <FormControlLabel
        style={{ margin: 0 }}
        labelPlacement="end"
        label={column.label}
        disabled={disabled !== undefined ? disabled : undefined}
        control={
          <Checkbox
            name={'all'}
            disabled={disabled !== undefined ? disabled : undefined}
            color={checkboxColor}
            checked={isCheckedAll}
            indeterminate={indeterminateCheck}
            onChange={(event) => handleCheckedAll && handleCheckedAll(event)}
          />
        }
      />
    );
  }
  if (column.apiField === 'customCheckbox') {
    return (
      <TableCheckbox
        checked={isCheckedAll}
        disabled={disabled !== undefined ? disabled : undefined}
        indeterminate={indeterminateCheck}
        onChange={(event) => handleCheckedAll && handleCheckedAll(event)}
      />
    );
  }

  if (column.showTooltip) {
    return <span title={column.label}>{column.label}</span>;
  }

  if (column.showCollapsed) {
    return (
      <Box display={'flex'} justifyContent={'flex-start'} alignItems="center">
        {column.collapsed ? (
          <Tooltip title={`Mostrar columna "${column.label}"`}>
            <IconButton
              size="small"
              onClick={() => handleColumnCollapsed(column)}
            >
              <AddIcon />
            </IconButton>
          </Tooltip>
        ) : (
          <Tooltip title={'Ocultar columna'}>
            <IconButton
              size="small"
              onClick={() => handleColumnCollapsed(column)}
            >
              <RemoveIcon />
            </IconButton>
          </Tooltip>
        )}
        <span>{column.collapsed ? '' : column.label}</span>
      </Box>
    );
  }

  return (
    <>
      {column.label}
      <MultipleSort column={column} />
    </>
  );
};

export default CustomTableCellBody;
